/**
 * PDFReceiptDropzone — F-008 Phase 6 (IMPL-P6-001).
 *
 * Wrapper sottile attorno al `PDFDropzone` refactored di F-003.4 + F-008
 * Phase 5. Inietta i 4 prop opzionali (`getUploadUrl`, `confirmUpload`,
 * `ariaLabel`, `maxSizeBytes`) per il flusso operatore di upload ricevuta:
 *
 *  - `getUploadUrl` → `operatorClient.presignRicevutaUpload`
 *  - `confirmUpload`→ delegato al caller via `onFileReady` (il commit vero
 *                     avviene in `useUploadRicevuta.mutate(...)` dal modal,
 *                     dopo che l'operatore ha compilato protocollo / data /
 *                     firma)
 *  - `ariaLabel`    → "Carica ricevuta firmata digitalmente (PDF)"
 *  - `maxSizeBytes` → `RICEVUTA_MAX_SIZE_BYTES` (10 MiB) — coerente con il
 *                     vincolo backend (`presignRicevutaInputSchema`)
 *
 * Pattern di riferimento: PHASE_6_PLAN.md IMPL-P6-001 e
 * `apps/web/src/features/pratica/components/PDFDropzone.tsx` (commenti
 * "F-008 Phase 5 — Injectable props").
 */
import { PDFDropzone } from '../../pratica/components/PDFDropzone';
import { operatorClient } from '../lib/operator-client';
import { RICEVUTA_MAX_SIZE_BYTES } from '@app/shared';

export interface PDFReceiptDropzoneProps {
  /** ID pratica per cui caricare la ricevuta (input del presign call). */
  praticaId: string;
  /**
   * Chiamato quando l'upload S3 è completo: il caller stasha
   * `{ s3Key, sha256, sizeBytes }` nello state del form per il successivo
   * commit (`useUploadRicevuta.mutate(...)`).
   */
  onFileReady: (info: {
    s3Key: string;
    sha256: string;
    sizeBytes: number;
  }) => void;
  /** Callback opzionale per errori del dropzone (network, validation, S3). */
  onError?: (msg: string) => void;
  /** Disabilita il dropzone (es. mentre la mutation di commit è in flight). */
  disabled?: boolean;
}

export function PDFReceiptDropzone({
  praticaId,
  onFileReady,
  onError,
}: PDFReceiptDropzoneProps) {
  return (
    <PDFDropzone
      praticaId={praticaId}
      ariaLabel="Carica ricevuta firmata digitalmente (PDF, max 10 MB)"
      maxSizeBytes={RICEVUTA_MAX_SIZE_BYTES}
      variant="full"
      getUploadUrl={async (file) => {
        const res = await operatorClient.presignRicevutaUpload({
          praticaId,
          contentType: 'application/pdf',
          sizeBytes: file.size,
        });
        return { uploadUrl: res.uploadUrl, s3Key: res.s3Key };
      }}
      confirmUpload={async ({ s3Key, sha256, sizeBytes }) => {
        onFileReady({ s3Key, sha256, sizeBytes });
      }}
      onUploaded={() => {
        // Retro-compat: il commit avviene nel modal, non qui.
      }}
      onError={onError}
    />
  );
}
